import { inject, Injectable, OnDestroy } from '@angular/core'
import { BehaviorSubject, from, Observable, Subscription } from 'rxjs'
import { FeathersService } from '../../feathers.service'
import { map, shareReplay, switchMap, tap } from 'rxjs/operators'
import { PrivateMessage, PrivateMessageQueryType } from '../../../models/privateMessage'
import { groupBy } from '../../utils'
import { User } from '../../../models/user'
import { UsersService } from '../users.service'
import { InboundPrivateMessageQuery, OutboundPrivateMessageQuery } from './PrivateMessageQuery'
import { throunceTime } from '../../../shared/utils/rxjsUtils'

@Injectable({
  providedIn: 'root',
})
export class PrivateMessagesService implements OnDestroy {
  private feathers = inject(FeathersService)
  private userService = inject(UsersService)

  TAG: string = 'PrivateMessagesService'
  allStream$: Observable<PrivateMessage[]>

  unreadCountForCurrentUser$$ = new BehaviorSubject<void>(void 0)
  unreadCountForCurrentUser$ = this.unreadCountForCurrentUser$$.pipe(
    throunceTime(5000),
    switchMap((_) => this.userService.getCurrentUser()),
    switchMap((user) =>
      from(
        this.feathers.count(PrivateMessage.serviceName, {
          recipientId: user.id,
          isRead: { $or: [false, null] },
          queryType: PrivateMessageQueryType.countUnread,
        }),
      ),
    ),
    // tap(e => console.log('unreadCountUpdate', e)),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  private currentUserInboundMessagesSub: Subscription
  private currentUserOutboundMessagesSub: Subscription

  constructor() {
    this.updateUnreadCount()
  }

  getAllWithCaching(): Observable<PrivateMessage[]> {
    if (!this.allStream$) this.initAllStream()
    return this.allStream$
  }

  getInboundForCurrentUserPaginated(
    limit: number = 20,
    opts?: InboundPrivateMessageQuery,
  ): Observable<PrivateMessage[]> {
    let currentUser: User

    return this.feathers.getCurrentUser().pipe(
      switchMap((user) => {
        currentUser = user
        return this.feathers.find(PrivateMessage, {
          ...(opts?.type && { queryType: opts.type }),
          ...(opts?.concernedClientId && {
            concernedClientId: opts?.concernedClientId,
          }),
          ...(opts?.concernedAssistantId && {
            concernedAssistantId: opts?.concernedAssistantId,
          }),
          ...(opts?.senderUserId && { userId: opts?.senderUserId }),
          ...(opts?.onlyUnread && { onlyUnread: opts?.onlyUnread }),
          recipientId: currentUser.id,
          $limit: limit,
          $skip: opts.offset,
          $sort: {
            publishedAt: -1,
          },
        }) as Observable<PrivateMessage[]>
      }),
      map((privMsgs) => privMsgs.filter((privMsg) => privMsg.recipientId === currentUser.id)),
      map((messages) => this.groupMessagesByThread(messages)),
      tap((_) => this.updateUnreadCount()),
      shareReplay({ bufferSize: 1, refCount: true }),
    )
  }

  private groupMessagesByThread(messages: PrivateMessage[]): PrivateMessage[] {
    const messagesGroupedByParentMessageId = groupBy(messages, 'parentMessageId')
    const msgs: PrivateMessage[] = []

    for (const parentId in messagesGroupedByParentMessageId) {
      if (messagesGroupedByParentMessageId.hasOwnProperty(parentId)) {
        const messageGroup: PrivateMessage[] = messagesGroupedByParentMessageId[parentId]
        if (parentId === 'null') {
          for (const m of messageGroup) {
            if (!messagesGroupedByParentMessageId.hasOwnProperty(m.id)) msgs.push(m)
          }
          continue
        }
        const latest = messageGroup.reduce(
          (prev, current) => (prev?.publishedAt > current?.publishedAt ? prev : current),
          null,
        )
        msgs.push(latest)
      }
    }
    return msgs
  }

  getOutboundForCurrentUserPaginated(limit: number = 20, opts?: OutboundPrivateMessageQuery) {
    let currentUser: User

    return this.userService.getCurrentUser().pipe(
      switchMap((user) => {
        currentUser = user
        return this.feathers
          .find(PrivateMessage, {
            ...(opts?.type && { queryType: opts.type }),
            // ...opts?.concernedClientId && { concernedClientId: opts?.concernedClientId},
            // ...opts?.concernedAssistantId && { concernedAssistantId: opts?.concernedAssistantId},
            // ...opts?.onlyUnread && {onlyUnread: opts?.onlyUnread},
            $limit: limit,
            $skip: opts.offset,
            $sort: {
              publishedAt: -1,
            },
            userId: user?.id,
          })
          .pipe(map((msgs: PrivateMessage[]) => msgs.filter((msg) => msg.userId === user.id)))
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    )
  }

  public getUnreadCountForCurrentUser() {
    return this.unreadCountForCurrentUser$
  }

  async send(msg: PrivateMessage) {
    return await this.feathers.createWithFactory(PrivateMessage, msg)
  }

  public markMessageRead(message: PrivateMessage) {
    if (message.isRead) return
    message.isRead = true
    this.update(message)
  }

  public async markMessagesRead(ids: number[]) {
    return await this.patch(ids, { isRead: true })
  }
  public async markMessagesUnread(ids: number[]) {
    return await this.patch(ids, { isRead: false })
  }
  public async markAllMessagesRead() {
    const user = await this.feathers.getCurrentUserPromise()
    return await this.feathers.patchMultiByQuery(
      PrivateMessage,
      { recipientId: user.id, isRead: { $or: [false, null] } },
      { isRead: true },
    )
  }

  private async update(message: PrivateMessage) {
    return await this.feathers.upsertWithFactory(PrivateMessage, message)
  }

  private async patch(ids: number[], record: Partial<PrivateMessage>) {
    return await this.feathers.patchMultiByIds(PrivateMessage, ids, record)
  }

  private initAllStream() {
    this.allStream$ = this.feathers.find(PrivateMessage, {}).pipe(shareReplay({ bufferSize: 1, refCount: true }))
  }

  ngOnDestroy(): void {
    this.currentUserInboundMessagesSub?.unsubscribe()
    this.currentUserOutboundMessagesSub?.unsubscribe()
  }

  private updateUnreadCount() {
    console.log(`${this.TAG}#updateUnreadCount`)

    // this.getUnreadCountUpdateForCurrentUser()
    //   .pipe(
    //     first(),
    //   )
    //   .subscribe(count => this.unreadCountForCurrentUser$$.next(count))
    this.unreadCountForCurrentUser$$.next()
  }
  // debounceTime(5000),

  private getUnreadCountUpdateForCurrentUser() {
    return this.userService.getCurrentUser().pipe(
      switchMap((user) =>
        from(
          this.feathers.count(PrivateMessage.serviceName, {
            recipientId: user.id,
            isRead: { $or: [false, null] },
            queryType: PrivateMessageQueryType.countUnread,
          }),
        ),
      ),
      tap((e) => console.log('unreadCountUpdate', e)),
    )
  }
}
